import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Input from '../../components/form-control/Input';
import Link from '../../components/Link';
import { DASHBOARD, FORGOT_PASSWORD } from '../../resources/links';
import { getInsurerProfile, loginWithPassword } from '../../api/requests';
import { ICountry, ILoginDTO } from '../../resources/interfaces';
import useUserAccount from '../../hooks/userAccountHook';
import useRoute from '../../hooks/routesHook';
import MessagePrompt from '../../components/elements/MessagePrompt';
import { User } from '../../models/User';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { SET_COMPANY_ONBOARDING_STATUS, SET_PENDING_ACTIONS, getRedirectURL, getSessionStatus } from '../../states/slices/globalSlice';
import { getPreviousUserId } from '../../states/slices/userAccountSlice';
import { useMutation } from '@tanstack/react-query';
import { HiOutlineEnvelope } from 'react-icons/hi2';
import { LuLock } from 'react-icons/lu';
import { getCountriesFromState, SET_SELECTED_COUNTRY } from '../../states/slices/resourceSlice';
import Loader from '../../components/Loader';
import usePartner from '../../hooks/partnerHook';
import useAuth from '../../hooks/authHook';

function SignIn() {
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const countriesInState = useSelector(getCountriesFromState);
  const [countries, setCountries] = useState<ICountry[]>(countriesInState);
  const intendedRoute = params.get('redirect');
  const { getAccount, setProfile, updateInsurerInState, setAccount } = useUserAccount();
  const { fetchCompanyOnboardingStatus } = usePartner();
  const [loginError, setLoginError] = useState<string>();
  const { getDashboardBase } = useRoute();
  const sessionExpired = useSelector(getSessionStatus);
  const redirectURL = useSelector(getRedirectURL);
  const userId = useSelector(getPreviousUserId);
  const dispatch = useDispatch();
  const { isInsurerOnboarding } = useAuth();

  useEffect(() => {
    if (getAccount().isAuthenticated) {
      navigate(`${getDashboardBase()}/dashboard`, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (sessionExpired) {
      setLoginError('Account session expired due to inactivity. Kindly log in again');
    }
  }, [sessionExpired]);

  const { mutate: handleLogin, isPending: submitting } = useMutation({
    mutationFn: (payload: any) => loginWithPassword(payload),
    onSuccess: (response: any) => {
      setAccount({
        isNew: response?.data?.isNew ?? false,
        accessRight: response?.data?.profile?.accessRight,
        accountSession: '',
        userId: response?.data?.profile?.id,
        token: response?.data?.token,
        sessionExpired: false
      });

      handleProfileDetails(response?.data?.profile);

      if (new User(response?.data?.profile)?.isExternalSuper() || new User(response?.data?.profile)?.isInternalOwner()) {
        getCompanyPendingActions();
      }

    },
    onError: (error: any) => {
      setLoginError(error?.description);
    }
  });

  const getRouteForSignedInUser = (responseData: any) => {
    if (!responseData?.id) return DASHBOARD;

    const isSameUser = responseData.id === userId;
    if (intendedRoute) return intendedRoute;
    if (redirectURL && !redirectURL.includes('complete-account') && isSameUser) return redirectURL;
    return DASHBOARD;
  };

  const handleProfileDetails = (data: any) => {
    const selectedCountryItem = countries?.find(c => c.name === data?.country);
    if (selectedCountryItem) {
      dispatch(SET_SELECTED_COUNTRY(selectedCountryItem));
    }

    setProfile(data);

    if (new User(data)?.isInsurer()) {
      getInsurerProfileDetails();
    }

    const signedInRoute = getRouteForSignedInUser(data);
    navigate(signedInRoute, { replace: true });
  };

  const getInsurerProfileDetails = async () => {
    try {
      const response: any = await getInsurerProfile()
      if (response) {
        updateInsurerInState(response);
      }
    } catch (error) {

    }
  };

  const getCompanyPendingActions = async () => {
    if (!isInsurerOnboarding() && !window.location.pathname.includes('/store/preview')) {
      const data = await fetchCompanyOnboardingStatus();
      if (data) {
        dispatch(SET_PENDING_ACTIONS(data?.pendingActions));
        dispatch(SET_COMPANY_ONBOARDING_STATUS(data))
      }
    }
  }

  return (
    <div className={clsx('w-full')}>
      <div className="">

        {loginError && (
          <MessagePrompt type="error" message={loginError} showInfoIcon={false} />
        )}
      </div>

      <Formik
        initialValues={{
          email: '',
          password: '',
          rememberMe: false
        }}
        validateOnChange
        validateOnBlur
        validationSchema={Yup.object({
          email: Yup.string().trim().email('Invalid email').required('Email is required'),
          password: Yup.string().trim().required('Password is required'),
          rememberMe: Yup.boolean()
        })}
        onSubmit={async (values) => {
          const payload: ILoginDTO = {
            email: values.email.trim(),
            password: values.password
          };

          handleLogin(payload);
        }}>
        {({ values, errors, handleSubmit, handleChange, setFieldValue, touched }) => (
          <form className=" " onSubmit={handleSubmit}>
            <Input
              label="Email"
              name="email"
              required
              value={values.email.toLowerCase()}
              prepend={<HiOutlineEnvelope className="w-6 h-6 " />}
              onChange={handleChange}
              error={touched.email ? errors.email : undefined}
              helperText={touched.email ? errors.email : undefined}
              placeholder="Enter your email address"
              labelClasses='!text-gray-700'
            />

            <Input
              name="password"
              label="Password"
              value={values.password}
              type="password"
              wrapperClasses="my-5"
              showPasswordIcon
              required
              prepend={<LuLock className="w-6 h-6" />}
              onChange={handleChange}
              error={touched.password ? errors.password : undefined}
              helperText={touched?.password ? errors.password : undefined}
              placeholder="Enter your password"
              labelClasses='!text-gray-700'
            />

            <div className="flex items-center justify-end mb-8 mt-2">
              {/* <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  id="remember-me"
                  name="remember-me"
                  checked={values.rememberMe}
                  onChange={(e) => {
                    setFieldValue('rememberMe', e.target.checked);
                  }}
                />
                <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-gray-700"></div>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  Remember me
                </span>
              </label> */}

              <div className="text-sm text-[#004E60] transition-colors">
                <Link to={FORGOT_PASSWORD} label={'Forgot password?'} />
              </div>
            </div>

            <button
              type="submit"
              className="w-full flex justify-center items-center px-6 !py-3 !text-lg font-medium rounded-full border border-transparent text-white bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-900 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-gray-500/20 hover:shadow-gray-500/30"
              disabled={submitting}
            >{submitting ? <Loader size='xs' /> : 'Sign in to your account'}</button>
          </form>
        )}
      </Formik>
    </div>
  );
}

export default SignIn;
